import Vue from "vue";
import {
  collection,
  query,
  where,
  limit,
  getDocs,
  orderBy,
} from "firebase/firestore";
import {
  MATCHES_CANONICAL_COLLECTION,
  canonicalAthleteIdEqualityVariants,
} from "./canonicalMatches";

const STORAGE_KEY = "rankster-ui-season";
/** Set when the user explicitly picks "All" in the season menu so we don’t remap it to the current year. */
const WANTS_ALL_SEASONS_KEY = "rankster-ui-wants-all-seasons";

/** In-memory cache so we don’t run 30+ queries on every navigation. */
let seasonsWithDataCache = { at: 0, items: null };
const SEASONS_CACHE_MS = 4 * 60 * 1000;

/** Short label e.g. 2025-26 (July–June scholastic year). */
export function currentSeasonLabelShort(d = new Date()) {
  const y = d.getFullYear();
  const m = d.getMonth();
  if (m >= 6) return `${y}-${String(y + 1).slice(-2)}`;
  return `${y - 1}-${String(y).slice(-2)}`;
}

/** July 1 of the calendar year when this scholastic label begins (e.g. 2025-26 → 2025-07-01 local). */
function scholasticYearStartDate(shortNorm) {
  const m = String(shortNorm).match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const yStart = parseInt(m[1], 10);
  if (Number.isNaN(yStart)) return null;
  return new Date(yStart, 6, 1, 0, 0, 0, 0);
}

/** Start calendar year of label "2025-26" → 2025. */
function scholasticLabelStartYear(shortNorm) {
  const m = String(shortNorm).match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  return Number.isNaN(y) ? null : y;
}

/** True when stored YYYY-YY is an earlier scholastic year than the current one (e.g. still on 2024-25 in 2025-26). */
function storedSeasonIsBehindCurrentScholasticYear(normalized) {
  if (!normalized || normalized === "All") return false;
  const cur = currentSeasonLabelShort();
  if (normalized === cur) return false;
  const sy = scholasticLabelStartYear(normalized);
  const cy = scholasticLabelStartYear(cur);
  if (sy == null || cy == null) return false;
  return sy < cy;
}

function loadStoredSeason() {
  const persist = (v) => {
    try {
      localStorage.setItem(STORAGE_KEY, v);
    } catch (_e) {
      /* noop */
    }
  };

  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s != null && String(s).trim() !== "") {
      const raw = String(s).trim();
      const originalNorm = normalizeSeasonToShort(raw) || "All";
      const wantsAll =
        typeof localStorage !== "undefined" &&
        localStorage.getItem(WANTS_ALL_SEASONS_KEY) === "1";

      if (originalNorm === "All" && !wantsAll) {
        const v = currentSeasonLabelShort();
        persist(v);
        return v;
      }

      let v = originalNorm;
      if (v !== "All") {
        const start = scholasticYearStartDate(v);
        if (
          start &&
          !Number.isNaN(start.getTime()) &&
          Date.now() < start.getTime()
        ) {
          v = currentSeasonLabelShort();
        } else if (storedSeasonIsBehindCurrentScholasticYear(v)) {
          v = currentSeasonLabelShort();
        }
      }
      if (v !== originalNorm || raw !== v) {
        persist(v);
      }
      return v;
    }
  } catch (_e) {
    /* noop */
  }
  const def = currentSeasonLabelShort();
  persist(def);
  return def;
}

/**
 * Collapse stored Season strings to one canonical short key (YYYY-YY) for the menu + queries.
 * Accepts e.g. 2025-26, 2025-2026, 2025–2026 (en dash).
 */
export function normalizeSeasonToShort(raw) {
  if (raw == null) return "";
  let t = String(raw).trim();
  if (/^all$/i.test(t)) return "All";
  const long = t.match(/^(\d{4})\s*[-–/]\s*(\d{4})$/);
  if (long) {
    const a = parseInt(long[1], 10);
    const b = parseInt(long[2], 10);
    if (b === a + 1) return `${a}-${String(b).slice(-2)}`;
  }
  const short = t.match(/^(\d{4})-(\d{2})$/);
  if (short) return t;
  return t;
}

/** UI label: canonical short YYYY-YY (same as import / Firestore convention). */
export function seasonShortToDisplay(shortLabel) {
  if (!shortLabel || shortLabel === "All") return "All";
  const n = normalizeSeasonToShort(String(shortLabel).trim());
  return n || String(shortLabel);
}

/**
 * Values that might appear in Firestore Season for this scholastic year (short + long year).
 */
export function seasonShortToStoredVariants(selected) {
  if (isAllSeasons(selected)) return [];
  const norm = normalizeSeasonToShort(selected);
  if (!norm || norm === "All") return [];
  const m = norm.match(/^(\d{4})-(\d{2})$/);
  if (!m) return [norm];
  const y1 = parseInt(m[1], 10);
  const y2 = y1 + 1;
  return Array.from(new Set([norm, `${y1}-${y2}`]));
}

/** Reactive selected season for toolbar: "All" or e.g. "2025-26". */
export const seasonFilterState = Vue.observable({
  selected: loadStoredSeason(),
});

export function setSeasonFilter(value) {
  let v = value != null ? String(value).trim() : "All";
  if (v && v !== "All") {
    const n = normalizeSeasonToShort(v);
    if (n && n !== "All") v = n;
  }
  seasonFilterState.selected = v || "All";
  try {
    localStorage.setItem(STORAGE_KEY, seasonFilterState.selected);
    if (seasonFilterState.selected === "All") {
      localStorage.setItem(WANTS_ALL_SEASONS_KEY, "1");
    } else {
      localStorage.removeItem(WANTS_ALL_SEASONS_KEY);
    }
  } catch (_e) {
    /* noop */
  }
}

/**
 * Toolbar list: always includes the current scholastic year; "All" first; years
 * descending (newest first).
 */
export function mergeSeasonToolbarList(items) {
  const base =
    items && Array.isArray(items) && items.length ? [...items] : ["All"];
  const active = currentSeasonLabelShort();
  if (active && !base.includes(active)) {
    base.push(active);
  }
  const all = base.filter((s) => s === "All");
  const rest = base
    .filter((s) => s !== "All")
    .sort((a, b) => {
      const ka = seasonSortKey(a);
      const kb = seasonSortKey(b);
      if (kb !== ka) return kb - ka;
      return String(b).localeCompare(String(a));
    });
  return [...all, ...rest];
}

/**
 * Canonical labels to test for match data (e.g. 2025-26).
 * Includes the *next* scholastic year (e.g. 2026-27 while still in spring 2026)
 * so early-entered or next-season tags are discoverable.
 */
export function buildCanonicalSeasonCandidates(yearsBack = 32) {
  const items = [];
  const now = new Date();
  const startY =
    now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  const nextY = startY + 1;
  items.push(`${nextY}-${String(nextY + 1).slice(-2)}`);
  for (let i = 0; i < yearsBack; i++) {
    const y = startY - i;
    const label = `${y}-${String(y + 1).slice(-2)}`;
    if (!items.includes(label)) items.push(label);
  }
  return items;
}

/** Same July–June rule as match imports / Add match default. */
export function seasonLabelFromMatchDate(d) {
  if (!d || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = d.getMonth();
  if (m >= 6) return `${y}-${String(y + 1).slice(-2)}`;
  return `${y - 1}-${String(y).slice(-2)}`;
}

function matchDateToDate(md) {
  if (!md) return null;
  try {
    if (typeof md.toDate === "function") return md.toDate();
    if (md instanceof Date) return md;
    if (typeof md.seconds === "number") {
      const d = new Date(md.seconds * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(md);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (_e) {
    return null;
  }
}

function seasonStringsFromMatchDoc(data) {
  const out = new Set();
  if (!data) return out;
  const raw = data.Season;
  if (raw != null && String(raw).trim() !== "") {
    const n = normalizeSeasonToShort(String(raw).trim());
    if (n && n !== "All") out.add(n);
  }
  const d = matchDateToDate(data.MatchDate);
  if (d) {
    const derived = seasonLabelFromMatchDate(d);
    if (derived) out.add(derived);
  }
  return out;
}

function seasonSortKey(label) {
  const m = String(label).match(/^(\d{4})/);
  return m ? parseInt(m[1], 10) : 0;
}

/**
 * Seasons that appear on at least one match doc (collection group).
 * Includes canonical years plus any non-standard Season strings seen on recent matches.
 */
export async function fetchSeasonsThatHaveMatches(db, { force = false } = {}) {
  const now = Date.now();
  if (
    !force &&
    seasonsWithDataCache.items &&
    seasonsWithDataCache.items.length > 1 &&
    now - seasonsWithDataCache.at < SEASONS_CACHE_MS
  ) {
    return seasonsWithDataCache.items;
  }

  const withData = new Set();

  try {
    const recentSnap = await getDocs(
      query(
        collection(db, MATCHES_CANONICAL_COLLECTION),
        orderBy("MatchDate", "desc"),
        limit(300)
      )
    );
    recentSnap.forEach((d) => {
      seasonStringsFromMatchDoc(d.data()).forEach((s) => withData.add(s));
    });
  } catch (_e) {
    /* missing index or rules */
  }

  const candidates = buildCanonicalSeasonCandidates(32);
  const batchSize = 8;
  for (let i = 0; i < candidates.length; i += batchSize) {
    const chunk = candidates.slice(i, i + batchSize);
    await Promise.all(
      chunk.map(async (season) => {
        const variants = seasonShortToStoredVariants(season);
        if (!variants.length) return;
        // Use equality per stored shape — `in` on collection groups often lacks an index and fails silently.
        for (const v of variants) {
          try {
            // Must include orderBy(MatchDate) so Firestore uses the composite
            // (Season + MatchDate) index; where+limit alone often has no index.
            const snap = await getDocs(
              query(
                collection(db, MATCHES_CANONICAL_COLLECTION),
                where("Season", "==", v),
                orderBy("MatchDate", "desc"),
                limit(1)
              )
            );
            if (!snap.empty) {
              withData.add(season);
              break;
            }
          } catch (_e) {
            /* per-query index / permission */
          }
        }
      })
    );
  }

  const list = Array.from(withData).filter(Boolean);
  const activeScholastic = currentSeasonLabelShort();
  if (activeScholastic && !list.includes(activeScholastic)) {
    list.push(activeScholastic);
  }
  list.sort((a, b) => {
    const ka = seasonSortKey(a);
    const kb = seasonSortKey(b);
    if (kb !== ka) return kb - ka;
    return String(b).localeCompare(String(a));
  });

  const result = ["All", ...list];
  if (list.length > 0) {
    seasonsWithDataCache = { at: now, items: result };
  }
  return result;
}

export function invalidateSeasonsWithDataCache() {
  seasonsWithDataCache = { at: 0, items: null };
}

export function isAllSeasons(s) {
  return !s || String(s).trim() === "" || String(s).trim() === "All";
}

/**
 * True if this athlete has any canonical bout whose `Season` matches stored variants.
 * Uses WinnerAthleteId / LoserAthleteId (and id type variants); ParticipantAthleteIds as fallback.
 */
export async function athleteHasCanonicalMatchInSeason(
  db,
  athleteId,
  seasonVariants
) {
  const ref = collection(db, MATCHES_CANONICAL_COLLECTION);
  const vars = [...new Set((seasonVariants || []).filter(Boolean))].slice(
    0,
    10
  );
  if (!vars.length) return false;
  const idVars = canonicalAthleteIdEqualityVariants(athleteId);
  if (!idVars.length) return false;

  const querySide = async (field, idVal) => {
    try {
      const snap = await getDocs(
        query(
          ref,
          where(field, "==", idVal),
          where("Season", "in", vars),
          orderBy("MatchDate", "desc"),
          limit(1)
        )
      );
      return !snap.empty;
    } catch (_e) {
      try {
        const snap = await getDocs(
          query(
            ref,
            where(field, "==", idVal),
            where("Season", "in", vars),
            limit(1)
          )
        );
        return !snap.empty;
      } catch (_e2) {
        return false;
      }
    }
  };

  for (const idVal of idVars) {
    const [w, l] = await Promise.all([
      querySide("WinnerAthleteId", idVal),
      querySide("LoserAthleteId", idVal),
    ]);
    if (w || l) return true;
  }

  for (const idVal of idVars) {
    try {
      const snap = await getDocs(
        query(
          ref,
          where("ParticipantAthleteIds", "array-contains", idVal),
          where("Season", "in", vars),
          orderBy("MatchDate", "desc"),
          limit(1)
        )
      );
      if (!snap.empty) return true;
    } catch (_e) {
      try {
        const snap = await getDocs(
          query(
            ref,
            where("ParticipantAthleteIds", "array-contains", idVal),
            where("Season", "in", vars),
            limit(1)
          )
        );
        if (!snap.empty) return true;
      } catch (_e2) {
        /* noop */
      }
    }
  }
  return false;
}

/**
 * Keep only athletes with at least one match in the given season (batched queries).
 */
export async function filterAthleteRowsBySeason(db, athleteRows, season) {
  if (isAllSeasons(season) || !athleteRows || athleteRows.length === 0) {
    return athleteRows || [];
  }
  const variants = seasonShortToStoredVariants(season);
  if (variants.length === 0) return athleteRows || [];
  const out = [];
  const chunkSize = 25;
  for (let i = 0; i < athleteRows.length; i += chunkSize) {
    const chunk = athleteRows.slice(i, i + chunkSize);
    const hits = await Promise.all(
      chunk.map(async (r) => {
        try {
          const ok = await athleteHasCanonicalMatchInSeason(db, r.id, variants);
          return ok ? r : null;
        } catch (_e) {
          return null;
        }
      })
    );
    hits.forEach((h) => {
      if (h) out.push(h);
    });
  }
  return out;
}
